import { useEffect, useMemo, useRef, useState } from "react";
import { Wheel } from "react-custom-roulette";
import type { Socket } from "socket.io-client";
import { Gamepad2, Skull, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { isCustomAppId, type CommonGame, type RoomMode } from "@/lib/api";
import { cn } from "@/lib/utils";

const SLICE_COLORS = [
  "#1e3a5f",
  "#0f4c75",
  "#3282b8",
  "#0a2540",
  "#274472",
  "#5c2a9d",
  "#1f6f8b",
  "#2c5364",
];

/** Shorter labels when there are many slices so text does not spill into neighbors. */
function trimForWheel(label: string, sliceCount: number): string {
  const max =
    sliceCount > 48 ? 8 : sliceCount > 36 ? 10 : sliceCount > 24 ? 14 : 26;
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

function fontSizeFor(count: number): number {
  if (count <= 12) return 14;
  if (count <= 24) return 11;
  if (count <= 48) return 8;
  if (count <= 96) return 6;
  return 5;
}

// Spin velocity tuning. react-custom-roulette's `spinDuration` is a multiplier
// where 1.0 = ~11350ms total spin time, so we work in ms and convert at render.
// The library is patched to use a near-linear deceleration curve (see
// scripts/patch-react-custom-roulette.mjs), so the wheel keeps speed for most
// of the animation and only creeps in at the very end. We pad the duration a
// bit to give that creep room to build tension.
const RANDOM_SPIN_MIN_MS = 5500;
const RANDOM_SPIN_MAX_MS = 10_500;
const DEFAULT_SPIN_MS = 7500;
const LIB_BASE_SPIN_MS = 11_350;
// Lower clamp on the rendered animation. Late joiners receive a much shorter
// `durationMs` so their wheel catches up to the in-flight spin instead of
// playing a full-length animation past the original landing time.
const ABS_MIN_SPIN_MS = 600;
const ABS_MAX_SPIN_MS = 13_000;

function randomSpinMs(): number {
  return Math.round(
    RANDOM_SPIN_MIN_MS + Math.random() * (RANDOM_SPIN_MAX_MS - RANDOM_SPIN_MIN_MS),
  );
}

function spinMsToDuration(ms: number): number {
  const clamped = Math.min(ABS_MAX_SPIN_MS, Math.max(ABS_MIN_SPIN_MS, ms));
  return clamped / LIB_BASE_SPIN_MS;
}

interface GameWheelProps {
  games: CommonGame[];
  disabled: Set<number>;
  socket: Socket;
  roomCode: string;
  meSteamId?: string;
  members: { steamId: string; displayName: string }[];
  mode: RoomMode;
  onModeChange: (mode: RoomMode) => void;
  onSpinningChange?: (spinning: boolean) => void;
}

interface SpinningPayload {
  initiatorSteamId: string;
  prizeIndex: number;
  durationMs: number;
  startedAt: number;
  slices: CommonGame[];
}

interface ResultPayload {
  initiatorSteamId: string;
  appId: number;
  name: string;
}

export function GameWheel({
  games,
  disabled,
  socket,
  roomCode,
  meSteamId,
  members,
  mode,
  onModeChange,
  onSpinningChange,
}: GameWheelProps) {
  const enabled = useMemo(
    () => games.filter((g) => !disabled.has(g.appId)),
    [games, disabled],
  );
  const [override, setOverride] = useState<CommonGame[] | null>(null);
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeIndex, setPrizeIndex] = useState(0);
  const [spinDurationMs, setSpinDurationMs] = useState(DEFAULT_SPIN_MS);
  const [winner, setWinner] = useState<{
    name: string;
    appId: number;
    iconUrl: string | null;
    custom: boolean;
  } | null>(null);
  const [spinningInitiator, setSpinningInitiator] = useState<string | null>(null);
  const [resultPulse, setResultPulse] = useState(false);
  const pulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // While a spin is in progress, lock the wheel to the spin's slice list so
  // every viewer sees the same wheel land on the same prize. Otherwise track
  // the current enabled games so toggles, adds, and removes show up live.
  const subset = mustSpin && override ? override : enabled;

  const data = useMemo(() => {
    if (subset.length === 0) {
      return [{ option: "—", style: { backgroundColor: "#1e3a5f", textColor: "#f8fafc" } }];
    }
    return subset.map((g, i) => ({
      option: trimForWheel(g.name, subset.length),
      style: {
        backgroundColor: SLICE_COLORS[i % SLICE_COLORS.length]!,
        textColor: "#f8fafc",
      },
    }));
  }, [subset]);

  // react-custom-roulette caches data in internal state and doesn't reliably
  // redraw when the data prop changes. Keying on the slice signature forces a
  // remount whenever the wheel contents change (but stays stable while a spin
  // is in flight, because subset is locked to override during mustSpin).
  const wheelKey = useMemo(
    () => `${subset.length}:${subset.map((g) => g.appId).join(",")}`,
    [subset],
  );

  // After a spin ends, `subset` may flip back to `enabled` while `prizeIndex`
  // still points at a slot from the (longer) override list — especially in
  // knockout mode where the winner gets removed. The wheel remounts with the
  // shorter data array and would crash reading `data[prizeIndex]` if we passed
  // the raw value, so clamp into the new range.
  const safePrizeIndex = Math.min(prizeIndex, Math.max(0, data.length - 1));

  // Idle alignment after a spin (esp. when the Wheel remounts with a new key,
  // e.g. knockout removes the winner slice). The library only applies this when
  // the data effect runs; must be -1 while spinning so it doesn't fight the animation.
  const startingOptionIndex = useMemo(() => {
    if (mustSpin || !winner || subset.length === 0) return -1;
    const atSpinIdx =
      prizeIndex >= 0 && prizeIndex < subset.length ? subset[prizeIndex] : undefined;
    if (atSpinIdx && atSpinIdx.appId === winner.appId) return prizeIndex;
    const idx = subset.findIndex((g) => g.appId === winner.appId);
    return idx >= 0 ? idx : 0;
  }, [mustSpin, winner, subset, prizeIndex]);

  // react-custom-roulette has two independent effects: one on `[data]` that
  // builds `prizeMap`, and one on `[mustStartSpinning]` that reads
  // `prizeMap[prizeNumber]`. On a fresh mount they run in the same flush, so
  // the spin effect reads the initial `prizeMap = [[0]]` and crashes when
  // `prizeNumber > 0` (`undefined[NaN]`). We MUST guarantee that any render
  // where the Wheel is (re)mounted has `mustStartSpinning=false`, then flip it
  // to true on a follow-up render once the library's data effect has had a
  // chance to commit `prizeMap`. Tracking the committed wheelKey via state
  // lets `readyToSpin` evaluate synchronously: as soon as `wheelKey` changes
  // for any reason (spin start with new slices, spin end shrinking the list,
  // toggle while idle), the comparison fails until our effect catches up.
  const [committedKey, setCommittedKey] = useState<string | null>(null);
  const readyToSpin = mustSpin && committedKey === wheelKey;
  useEffect(() => {
    setCommittedKey(wheelKey);
  }, [wheelKey]);

  useEffect(
    () => () => {
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
    },
    [],
  );

  useEffect(() => {
    onSpinningChange?.(mustSpin);
  }, [mustSpin, onSpinningChange]);

  useEffect(() => {
    function onSpinning(p: SpinningPayload) {
      if (!Array.isArray(p.slices) || p.slices.length === 0) return;
      if (
        typeof p.prizeIndex !== "number" ||
        !Number.isInteger(p.prizeIndex) ||
        p.prizeIndex < 0 ||
        p.prizeIndex >= p.slices.length
      )
        return;
      setOverride(p.slices);
      setPrizeIndex(p.prizeIndex);
      setSpinDurationMs(
        typeof p.durationMs === "number" && Number.isFinite(p.durationMs)
          ? p.durationMs
          : DEFAULT_SPIN_MS,
      );
      setMustSpin(true);
      setWinner(null);
      setSpinningInitiator(p.initiatorSteamId);
      setResultPulse(false);
    }
    function onResult(p: ResultPayload) {
      const meta = games.find((g) => g.appId === p.appId);
      setWinner({
        name: p.name,
        appId: p.appId,
        iconUrl: meta?.iconUrl ?? null,
        custom: meta?.custom ?? isCustomAppId(p.appId),
      });
    }
    socket.on("wheel:spinning", onSpinning);
    socket.on("wheel:result", onResult);
    return () => {
      socket.off("wheel:spinning", onSpinning);
      socket.off("wheel:result", onResult);
    };
  }, [socket, games]);

  function handleSpin() {
    if (enabled.length === 0 || mustSpin) return;
    const idx = Math.floor(Math.random() * enabled.length);
    socket.emit("wheel:spin", {
      code: roomCode,
      prizeIndex: idx,
      durationMs: randomSpinMs(),
      slices: enabled.map((g) => ({
        appId: g.appId,
        name: g.name,
        iconUrl: g.iconUrl,
      })),
    });
  }

  function onStopSpinning() {
    setMustSpin(false);
    const game = subset[prizeIndex];
    if (game && spinningInitiator && spinningInitiator === meSteamId) {
      socket.emit("wheel:result", {
        code: roomCode,
        appId: game.appId,
        name: game.name,
      });
    }
    if (game && !spinningInitiator) {
      setWinner({
        name: game.name,
        appId: game.appId,
        iconUrl: game.iconUrl,
        custom: game.custom ?? isCustomAppId(game.appId),
      });
    }
    setResultPulse(true);
    if (pulseTimer.current) clearTimeout(pulseTimer.current);
    pulseTimer.current = setTimeout(() => setResultPulse(false), 1800);
  }

  const initiatorName = useMemo(() => {
    if (!spinningInitiator) return null;
    return members.find((m) => m.steamId === spinningInitiator)?.displayName ?? "Someone";
  }, [spinningInitiator, members]);

  const isElimination = mode === "elimination";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Spin the wheel</span>
          <span className="text-xs font-normal text-muted-foreground">
            {enabled.length} of {games.length} enabled
          </span>
        </CardTitle>
        <div className="flex items-center gap-2 pt-1">
          <div className="inline-flex rounded-md border border-border/60 p-0.5">
            <button
              type="button"
              onClick={() => onModeChange("normal")}
              disabled={mustSpin}
              className={cn(
                "rounded px-2.5 py-1 text-xs font-medium transition-colors",
                !isElimination
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
                mustSpin && "cursor-not-allowed opacity-60",
              )}
              title="Spinning doesn't change the game list"
            >
              Classic
            </button>
            <button
              type="button"
              onClick={() => onModeChange("elimination")}
              disabled={mustSpin}
              className={cn(
                "flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium transition-colors",
                isElimination
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
                mustSpin && "cursor-not-allowed opacity-60",
              )}
              title="Each spin removes the winning game from the wheel"
            >
              <Skull className="h-3 w-3" />
              Knockout
            </button>
          </div>
          {isElimination && (
            <span className="text-xs text-muted-foreground">
              Winners get auto-disabled.
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4">
        {enabled.length === 0 && !mustSpin ? (
          <div className="flex h-72 w-full items-center justify-center rounded-md border border-dashed border-border/60 px-6 text-center text-sm text-muted-foreground">
            Enable at least one game from the list to spin.
          </div>
        ) : (
          <div
            className={cn(
              "transition-transform duration-700 ease-out",
              resultPulse ? "scale-105" : "scale-100",
            )}
          >
            <Wheel
              key={wheelKey}
              mustStartSpinning={readyToSpin}
              prizeNumber={safePrizeIndex}
              data={data}
              startingOptionIndex={startingOptionIndex}
              onStopSpinning={onStopSpinning}
              outerBorderColor="#0b1220"
              outerBorderWidth={6}
              innerRadius={12}
              innerBorderColor="#0b1220"
              radiusLineColor="#0b1220"
              radiusLineWidth={1}
              fontSize={fontSizeFor(subset.length)}
              textDistance={subset.length > 36 ? 54 : 62}
              spinDuration={spinMsToDuration(spinDurationMs)}
            />
          </div>
        )}

        {mustSpin && initiatorName && spinningInitiator !== meSteamId && (
          <p className="text-sm text-muted-foreground">{initiatorName} is spinning…</p>
        )}

        {winner && !mustSpin && (
          <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-primary">
            {winner.custom ? (
              <Sparkles className="h-4 w-4 shrink-0 text-amber-400" />
            ) : winner.iconUrl ? (
              <img
                src={winner.iconUrl}
                alt=""
                className="h-5 w-5 shrink-0 rounded-sm object-cover"
              />
            ) : (
              <Gamepad2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="font-medium">{winner.name}</span>
          </div>
        )}

        <Button
          className="w-full"
          size="lg"
          disabled={enabled.length === 0 || mustSpin}
          onClick={handleSpin}
        >
          {mustSpin ? "Spinning…" : isElimination ? "Spin & Eliminate" : "Spin"}
        </Button>
      </CardContent>
    </Card>
  );
}
